<template>
   <div class="h-125 [&_.ag-row]:cursor-pointer">
      <Grid
         id="organization-adminlog-grid"
         :rows="adminlog.data.value"
         :columns="[
            'type',
            'action',
            'table_name',
            'record_id',
            'changes',
            'user_email',
            'user_role',
            'created_at',
         ]"
         :loading="adminlog.isLoading.value"
         :columns-renderer="columnsRenderer"
         :selectable="false"
         @row-clicked="openEntry"
      />
   </div>

   <AdminLogModal v-model="modalOpen" :entry="selected" />
</template>

<script lang="ts" setup>
import type { Unpacked } from '@xleddyl/types'
import type { RowClickedEvent } from 'ag-grid-community'
import AdminLogChangesRenderer from '~/components/dashboard/grid/AdminLogChangesRenderer.vue'
import AdminLogTypeRenderer from '~/components/dashboard/grid/AdminLogTypeRenderer.vue'
import { useAdminLogFormat } from '~/composables/adminlog'
import { useAdminlog } from '~/composables/api/queries/adminlog'

type Entry = Unpacked<NonNullable<ReturnType<typeof useAdminlog>['data']['value']>>

const { t } = useI18n()
const organizationId = useOrganizationId()
const format = useAdminLogFormat()

const adminlog = useAdminlog(
   computed(() => ({
      organizationId: organizationId.value,
      enabled: !!organizationId.value,
   }))
)

const selected = ref<Entry>()
const modalOpen = ref<boolean>(false)

const openEntry = (event: RowClickedEvent<Entry>) => {
   if (!event.data) return
   selected.value = event.data
   modalOpen.value = true
}

const columnsRenderer = defineRenderers<Entry>({
   type: defineColumn.custom(AdminLogTypeRenderer, (data: Entry) => ({ type: data.type })),
   changes: defineColumn.custom(AdminLogChangesRenderer, (data: Entry) => ({
      labels: format.changesLabels(data.changes ?? []),
   })),
   action: { valueFormatter: (params) => format.actionLabel(params.value) },
   table_name: { valueFormatter: (params) => format.tableLabel(params.value) },
   record_id: { cellClass: 'font-mono' },
   user_email: { valueFormatter: (params) => format.userLabel(params.value) },
   user_role: {
      valueFormatter: (params) => (params.value ? t(`enum.${params.value}`) : '-'),
   },
   created_at: defineColumn.date('created_at'),
})
</script>
